// src/components/FloatingLanguageButton.jsx
import React, { useEffect, useCallback, useMemo, useState } from "react";

/** Load Google Website Translator (hidden widget). */
function useGoogleTranslate() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (window.google?.translate?.TranslateElement) {
      setLoaded(true);
      return;
    }

    // Hidden container for the widget (off-screen, not display:none)
    if (!document.getElementById("google_translate_element")) {
      const div = document.createElement("div");
      div.id = "google_translate_element";
      div.style.position = "fixed";
      div.style.bottom = "-9999px";
      div.style.right = "-9999px";
      div.style.width = "1px";
      div.style.height = "1px";
      div.style.opacity = "0";
      div.style.pointerEvents = "none";
      document.body.appendChild(div);
    }

    // Init callback
    window.googleTranslateElementInit = function () {
      try {
        new window.google.translate.TranslateElement(
          { pageLanguage: "en", includedLanguages: "en,hi", autoDisplay: false },
          "google_translate_element"
        );
        setLoaded(true);
      } catch {
        // swallow
      }
    };

    // Load the script once
    const existing = document.querySelector('script[src*="translate_a/element.js"]');
    if (!existing) {
      const s = document.createElement("script");
      s.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      s.async = true;
      document.head.appendChild(s);
    } else {
      const t = setInterval(() => {
        if (window.google?.translate?.TranslateElement) {
          window.googleTranslateElementInit?.();
          clearInterval(t);
        }
      }, 100);
      return () => clearInterval(t);
    }
  }, []);

  return loaded;
}

/** Cookie helpers */
function setCookie(name, value, domain) {
  const expires = new Date(Date.now() + 365 * 24 * 3600 * 1000).toUTCString(); // 1 year
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${value}; expires=${expires}; path=/; domain=${domain}; SameSite=Lax${secure}`;
}
function setGoogTrans(langFrom, langTo) {
  const v = `/${langFrom}/${langTo}`;
  const host = window.location.hostname;
  const parts = host.split(".");
  const topDomain = parts.length > 2 ? `.${parts.slice(-2).join(".")}` : `.${host}`;
  setCookie("googtrans", v, host);
  setCookie("googtrans", v, topDomain);
}
function getCurrentLang() {
  const m = document.cookie.match(/(?:^|;\s*)googtrans=([^;]+)/);
  if (!m) return "en";
  const val = decodeURIComponent(m[1]); // like "/en/hi"
  const parts = val.split("/");
  return parts[2] || "en";
}

/** Apply language by changing the hidden widget's <select> (no reload). */
function applyLanguageWithoutReload(target) {
  const tryApply = () => {
    const select = document.querySelector("#google_translate_element select.goog-te-combo");
    if (!select) return false;
    if (select.value !== target) {
      select.value = target;
    }
    // Trigger Google translate
    select.dispatchEvent(new Event("change"));
    return true;
  };

  // Try immediately; if not ready, retry for up to ~5s
  if (tryApply()) return;
  let tries = 0;
  const t = setInterval(() => {
    tries += 1;
    if (tryApply() || tries > 50) clearInterval(t);
  }, 100);
}

export default function FloatingLanguageButton() {
  const loaded = useGoogleTranslate();
  const [lang, setLang] = useState(() => localStorage.getItem("siteLang") || getCurrentLang());

  // Keep <html lang=""> accurate
  useEffect(() => {
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  // When widget is ready, apply saved/active language without reload
  useEffect(() => {
    if (!loaded) return;
    const saved = localStorage.getItem("siteLang") || lang || "en";
    setGoogTrans("en", saved);
    applyLanguageWithoutReload(saved);
    // ensure localStorage mirrors what we applied
    localStorage.setItem("siteLang", saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  const isHindi = lang === "hi";
  const buttonText = useMemo(
    () => (isHindi ? "Translate to English" : "हिंदी में अनुवाद करें"),
    [isHindi]
  );
  const aria = useMemo(
    () => (isHindi ? "Switch site language to English" : "Switch site language to Hindi"),
    [isHindi]
  );

  const toggleLang = useCallback(() => {
    const target = isHindi ? "en" : "hi";
    localStorage.setItem("siteLang", target);
    setLang(target);
    setGoogTrans("en", target);
    applyLanguageWithoutReload(target); // 👈 no reload
  }, [isHindi]);

  if (!loaded) return null;

  return (
    <div
      className="fixed z-[110] pointer-events-none"
      style={{
        bottom: "max(88px, calc(env(safe-area-inset-bottom) + 88px))",
        right: "max(16px, env(safe-area-inset-right))",
        overflow: "clip",
        contain: "layout paint",
        transform: "translateZ(0)",
      }}
    >
      <button
        type="button"
        onClick={toggleLang}
        aria-label={aria}
        title={aria}
        className="pointer-events-auto"
        style={{
          border: "none",
          borderRadius: "9999px",
          padding: "12px 16px",
          boxShadow: "0 8px 24px rgba(0,0,0,.18), 0 4px 12px rgba(0,0,0,.12)",
          background: "linear-gradient(135deg, #18367e, #2563eb)",
          color: "#fff",
          fontWeight: 700,
          fontSize: "14px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1 }}>A↔अ</span>
        <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
          <span>{buttonText}</span>
          <span style={{ opacity: 0.9, fontWeight: 500, fontSize: 11, marginTop: 2 }}>
            {isHindi ? "Current: हिंदी" : "Current: English"}
          </span>
        </span>
      </button>
    </div>
  );
}
